import React, { useEffect, useState } from 'react';
import ClayCard from '@clayui/card';
import ClayLayout from '@clayui/layout';
import ClayButton from '@clayui/button';
import './SubCategoriesPage.css';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import Pagination from '../../generic-components/Pagination/Pagination';
import { useApiMutation } from '../../apis/useApi';
import Breadcrumbs from '../Breadcrumbs/Breadcrumbs';
import Loader from './custom-loader/Loader';
import NoDataFound from '../../generic-components/NoDataFound/NoDataFound';
import { LANGUAGE } from '../../utils/languageProperties';

interface ServiceItem {
    id: string;
    title: string;
}

const SubCategoriesPage = () => {
    const { taxonomyCategoryIds, categoryId } = useParams<{
        taxonomyCategoryIds: string;
        categoryId: string;
    }>();

    const [subCategoryData, setSubCategoryData] = useState<ServiceItem[]>([]);
    const [allSubCategoryData, setAllSubCategoryData] = useState<ServiceItem[]>(
        []
    );
    const [filteredData, setFilteredData] = useState<ServiceItem[]>([]);

    const [categoryIds, setCategoryIds] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [isSubCategoryLoading, setIsSubCategoryLoading] = useState(false);
    const { searchValue, setSearchValue } = useOutletContext<any>();

    const selectedService = JSON.parse(
        sessionStorage.getItem('selectedServiceItem') || 'null'
    );
    const currentLocale =
        (window as any)?.Liferay?.ThemeDisplay?.getBCP47LanguageId?.() ||
        'en-US';

    const itemsPerPage = 6;
    const navigate = useNavigate();

    const scopeKey = 'BID Partner';

    const getPartnerOffers = useApiMutation<any, {}>(
        `o/c/partneroffers/scopes/${scopeKey}`,
        'get'
    );

    const isLoading = isSubCategoryLoading || getPartnerOffers?.isLoading;

    // Fetch SubCategories
    useEffect(() => {
        const fetchSubCategories = async () => {
            setIsSubCategoryLoading(true);

            const query = `
                {
                  headlessAdminTaxonomy_v1_0 {
                    taxonomyCategory(taxonomyCategoryId: "${taxonomyCategoryIds}") {
                      taxonomyCategories {
                        items {
                          id
                          name
                        }
                      }
                    }
                  }
                }
            `;

            try {
                const response = await fetch(
                    '/o/graphql?p_auth=' + (window as any)?.Liferay?.authToken,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'accept-language': (
                                window as any
                            )?.Liferay?.ThemeDisplay?.getBCP47LanguageId()
                        },
                        body: JSON.stringify({ query })
                    }
                );

                const result = await response.json();

                const items =
                    result?.data?.headlessAdminTaxonomy_v1_0?.taxonomyCategory
                        ?.taxonomyCategories?.items ?? [];

                const mappedItems: ServiceItem[] = items.map((item: any) => ({
                    id: item.id,
                    title: item.name
                }));

                setAllSubCategoryData(mappedItems);
                setSubCategoryData(mappedItems);
                setFilteredData(mappedItems);

                const ids = items.map((item: any) => item.id);
                setCategoryIds(ids);
            } catch (err) {
                console.error('Failed to fetch subcategories', err);
                setAllSubCategoryData([]);
                setSubCategoryData([]);
                setFilteredData([]);
                setCategoryIds([]);
            } finally {
                setIsSubCategoryLoading(false);
            }
        };

        if (taxonomyCategoryIds) {
            fetchSubCategories();
        }
    }, [taxonomyCategoryIds]);

    // Fetch Partner Offers and filter categories
    useEffect(() => {
        const fetchPartnerOffers = async () => {
            if (categoryIds.length === 0 || allSubCategoryData.length === 0) {
                setSubCategoryData([]);
                setFilteredData([]);
                return;
            }

            try {
                const result = await getPartnerOffers.mutateAsync({
                    data: {},
                    params: {
                        p_auth: (window as any)?.Liferay?.authToken,
                        filter: `(offerStatus ne 'suspendedServiceOffer' and offerStatus ne 'deactivatedServiceOffer' and offerStatus ne 'pendingOfferApprovalFromPartner' and offerStatus ne 'pendingOfferApprovalFromAccountManager' and offerStatus ne 'accountManagerNeedRevision' and offerStatus ne 'partnerNeedRevision') and taxonomyCategoryIds/any(t:t in (${categoryIds.join(
                            ','
                        )}))`,
                        pageSize: 0
                    }
                });

                const usedCategoryIds = new Set<number>();

                result?.items?.forEach((item: any) => {
                    item.taxonomyCategoryBriefs?.forEach((cat: any) => {
                        usedCategoryIds.add(cat?.taxonomyCategoryId);
                    });
                });

                const filteredCategories = allSubCategoryData.filter((cat) =>
                    usedCategoryIds.has(Number(cat.id))
                );

                setSubCategoryData(filteredCategories);
                setFilteredData(filteredCategories);
            } catch (error) {
                console.error('Failed to fetch partner offers', error);
            }
        };

        fetchPartnerOffers();
    }, [categoryIds, allSubCategoryData]);


    useEffect(() => {
        if (searchValue) {
            const filteredData = subCategoryData.filter((item) =>
                item.title.toLowerCase().includes(searchValue.toLowerCase())
            );
            setFilteredData(filteredData);
        } else {
            setFilteredData(subCategoryData);
        }
    }, [searchValue]);

    // Pagination logic
    const indexOfLastItem = currentPage * itemsPerPage;

    const currentItems = filteredData.slice(
        indexOfLastItem - itemsPerPage,
        indexOfLastItem
    );

    return (
        <div className="service-parent">
            <div className="service-container">
                <Breadcrumbs setSearchValue={setSearchValue} />

                <h2 className="block-heading">
                    {selectedService?.title || ''}
                </h2>

                {isLoading ? (
                    <Loader />
                ) : (
                    <>
                        {Boolean(currentItems?.length) ? (
                            <ClayLayout.Row className="service-card-container">
                                {currentItems.map((card) => (
                                    <ClayLayout.Col
                                        key={card.id}
                                        size={12}
                                        lg={6}
                                        className="flex justify-center"
                                    >
                                        <ClayCard className="service-card">
                                            <ClayCard.Body>
                                                <div className="service-card-content">
                                                    <h3 className="service-card-title">
                                                        {card.title}
                                                    </h3>

                                                    <ClayButton
                                                        displayType="link"
                                                        className="service-card-btn"
                                                        onClick={() => {
                                                            setSearchValue('');
                                                            sessionStorage.setItem(
                                                                'selectedSubServiceItem',
                                                                JSON.stringify({
                                                                    ...card,
                                                                    locale: currentLocale
                                                                })
                                                            );
                                                            navigate(
                                                                `/categories/${categoryId}/${taxonomyCategoryIds}/${card.id}`
                                                            );
                                                        }}
                                                    >
                                                        {
                                                            LANGUAGE[
                                                                'view-details'
                                                            ]
                                                        }
                                                    </ClayButton>
                                                </div>
                                            </ClayCard.Body>
                                        </ClayCard>
                                    </ClayLayout.Col>
                                ))}
                            </ClayLayout.Row>
                        ) : (
                            <NoDataFound />
                        )}

                        <Pagination
                            totalItems={subCategoryData.length}
                            itemsPerPage={itemsPerPage}
                            currentPage={currentPage}
                            onPageChange={setCurrentPage}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

export default SubCategoriesPage;